'use client'

import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import Link from 'next/link'
import { DOC_PATHS, DOC_VERSIONS } from '@/lib/legal'
import { YM_COUNTER_ID, setAnalyticsChoice, type AnalyticsChoice } from '@/lib/analytics'

// Плашка о cookie при первом визите (аудит .scratch/legal/01-audit-2026-09-19.md,
// п. 8). Пока номер счётчика Метрики не задан (lib/analytics.ts), все cookie
// строго необходимые: плашка только информирует, одна кнопка «Понятно».
// С номером счётчика плашка сообщает о Метрике (она уже работает) и даёт
// отключить её: две равные кнопки, выбор потом меняется в настройках.
//
// Отметка о закрытии — в localStorage с версией политики: поменяли текст
// политики (DOC_VERSIONS.cookies) — плашка покажется снова. localStorage может
// быть недоступен (приватный режим, запрет сайта) — тогда помним до перезагрузки.
// data-fab-avoid — чтобы кнопка репетитора уехала и не закрыла «Понятно».
// Inline-стили — как у TtsLimitToast (устаревший CSS на телефоне), кроме фона и
// рамки кнопки: их держит класс tk-btn-primary, иначе не будет hover.
//
// Крупный текст на телефоне (тикет 85): при zoom 1,3 на 320 px плашка занимала 92%
// экрана и закрывала задание. Там включается компактный вид (COMPACT_CSS): шрифт и
// отступы в css-px мельче (на экране всё равно крупнее обычного режима), текст
// согласия свёрнут в две строки, но целиком лежит в разметке (скринридер читает
// весь) и раскрывается кнопкой «Весь текст»; кнопки выбора в ряд. Формулировки
// не менялись. Стили тут, а не в globals.css: без нового CSS (устаревший кэш на
// телефоне) ряд «Весь текст» скрыт инлайном и плашка просто остаётся прежней.

const STORAGE_KEY = 'tk-cookie-notice'
const listeners = new Set<() => void>()
let dismissedInMemory = false

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

function isVisible(): boolean {
  if (dismissedInMemory) return false
  try {
    return localStorage.getItem(STORAGE_KEY) !== DOC_VERSIONS.cookies
  } catch {
    return true
  }
}

function dismiss(choice?: AnalyticsChoice) {
  if (choice) setAnalyticsChoice(choice)
  dismissedInMemory = true
  try {
    localStorage.setItem(STORAGE_KEY, DOC_VERSIONS.cookies)
  } catch {
    // не сохранилось — спрячем хотя бы до перезагрузки
  }
  listeners.forEach((l) => l())
}

export function CookieNotice() {
  const visible = useSyncExternalStore(subscribe, isVisible, () => false)
  const ref = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)

  // Пока плашка видна, под страницей резервируется место её высоты. Без этого
  // на телефоне она закрывает низ страницы (на /auth/sign-up — «Продолжить с
  // Google»), и прокрутить кнопку из-под плашки нельзя. Закрыли — отступ ушёл.
  useEffect(() => {
    const el = ref.current
    if (!visible || !el) return
    const body = document.body
    const previous = body.style.paddingBottom
    const reserve = () => {
      body.style.paddingBottom = `${el.offsetHeight + 24}px` // + отступ плашки от края и зазор
    }
    reserve()
    const observer = new ResizeObserver(reserve)
    observer.observe(el)
    return () => {
      observer.disconnect()
      body.style.paddingBottom = previous
    }
  }, [visible])

  if (!visible) return null
  return (
    <div
      ref={ref}
      role="region"
      aria-label="Уведомление о cookie"
      data-fab-avoid
      className="tk-cookie-notice"
      data-expanded={expanded ? '' : undefined}
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
        zIndex: 55,
        margin: '0 auto',
        maxWidth: 560,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px 16px',
        padding: '14px 16px',
        borderRadius: 16,
        border: '1px solid oklch(0.6 0.1 254 / 0.4)',
        background: 'linear-gradient(165deg, oklch(0.32 0.06 254 / 0.98) 0%, oklch(0.18 0.035 254 / 0.99) 100%)',
        boxShadow: '0 14px 32px -12px oklch(0 0 0 / 0.7)',
        color: 'oklch(0.92 0.03 240)',
        fontSize: 16,
        lineHeight: 1.45,
      }}
    >
      {/* Рост 999 : 1 — на широком экране кнопки остаются компактными справа,
          а на телефоне, перенесясь на свою строку, растягиваются на всю ширину. */}
      {YM_COUNTER_ID === null ? (
        <>
          <p style={{ flex: '999 1 260px', margin: 0 }}>
            Turkido использует только необходимые cookie — для входа в аккаунт и работы сервиса. Рекламы и слежки нет.{' '}
            <MoreLink />
          </p>
          <button
            type="button"
            onClick={() => dismiss()}
            className="tk-btn-primary"
            style={{ flex: '1 0 auto', padding: '10px 20px', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}
          >
            Понятно
          </button>
        </>
      ) : (
        <>
          <style>{COMPACT_CSS}</style>
          <p id="tk-cookie-text" className="tk-cookie-text" style={{ flex: '999 1 260px', margin: 0 }}>
            Turkido считает посещения через Яндекс Метрику, по ней мы видим, где в сервисе неудобно. Тексты, которые вы пишете, в Яндекс не уходят. Метрику можно отключить сейчас или потом в настройках.
            <span className="tk-cookie-more-inline">
              {' '}
              <MoreLink />
            </span>
          </p>
          {/* Только в компактном виде (COMPACT_CSS показывает ряд через !important). */}
          <div className="tk-cookie-more-row" style={{ display: 'none' }}>
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls="tk-cookie-text"
              onClick={() => setExpanded((v) => !v)}
              className="font-medium underline underline-offset-4"
              style={{ color: 'var(--tk-ice)', background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }}
            >
              {expanded ? 'Свернуть' : 'Весь текст'}
            </button>
            <MoreLink />
          </div>
          {/* Кнопки одинаковые: отключить должно быть так же легко, как закрыть. Блок сжимается
              до ширины плашки (flex-shrink 1, minWidth 0), иначе на телефоне кнопки
              стояли в ряд и «Только необходимые» вылезала за рамку (тикет 49).
              «Отключить Метрику» первой по просьбе владельца (задача 65 из Telegram). */}
          <div className="tk-cookie-choices" style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button type="button" onClick={() => dismiss('denied')} className="tk-pill" style={CHOICE_BTN}>
              Отключить Метрику
            </button>
            <button type="button" onClick={() => dismiss('granted')} className="tk-pill" style={CHOICE_BTN}>
              Понятно
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// Компактный вид: телефон (по ширине устройства, медиазапрос zoom не видит) и
// размер текста «Крупнее» или «Крупный». Размеры в css-px, на экране они x1,15-1,3:
// 14px шрифта = 16-18 px, не мельче обычного режима. На 320 px при 1,3 плашка
// ~150 css-px = 195 px экрана, треть от 568 (было 403 css-px, 92%).
// Тот же вид в обычном режиме на узком телефоне до 360 px (тикет 88: на 320x568
// было 52% экрана), но текст там остаётся 16 px, кнопки 15 px: zoom не увеличит.
const COMPACT = ['html.tk-zoom-115', 'html.tk-zoom-130']
const NARROW = ['html']
const sel = (roots: string[], tail: string) => roots.map((r) => `${r} ${tail}`).join(',\n  ')
const compactRules = (roots: string[], text: number, button: number) => `
  ${sel(roots, '.tk-cookie-notice')} {
    left: 8px !important;
    right: 8px !important;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 8px) !important;
    padding: 10px 12px !important;
    gap: 6px 8px !important;
    font-size: ${text}px !important;
    line-height: 1.35 !important;
  }
  ${sel(roots, '.tk-cookie-notice:not([data-expanded]) .tk-cookie-text')} {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }
  ${sel(roots, '.tk-cookie-more-inline')} {
    display: none;
  }
  ${sel(roots, '.tk-cookie-more-row')} {
    display: flex !important;
    flex: 1 1 100%;
    align-items: baseline;
    gap: 16px;
  }
  ${sel(roots, '.tk-cookie-choices')} {
    flex: 1 1 100% !important;
    flex-wrap: nowrap !important;
    gap: 6px !important;
  }
  ${sel(roots, '.tk-cookie-choices > button')} {
    flex: 1 1 0 !important;
    min-width: 0;
    padding: 7px 8px !important;
    font-size: ${button}px !important;
    line-height: 1.2;
  }`
const COMPACT_CSS = `@media (max-width: 360px) {${compactRules(NARROW, 16, 15)}
}
@media (max-width: 480px) {${compactRules(COMPACT, 14, 14)}
}`

const CHOICE_BTN = { flex: '1 0 auto', maxWidth: '100%', padding: '10px 20px', fontSize: 16, fontWeight: 700, whiteSpace: 'normal', textAlign: 'center', cursor: 'pointer' } as const

function MoreLink() {
  return (
    <Link
      href={DOC_PATHS.cookies}
      className="font-medium underline underline-offset-4"
      style={{ color: 'var(--tk-ice)' }}
    >
      Подробнее
    </Link>
  )
}
